import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, Mail, Phone, User, MessageSquare, Calendar, LogOut, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        setError('');
        fetchMessages();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Mot de passe incorrect');
      } else {
        setError('Erreur de connexion. Veuillez réessayer.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setMessages([]);
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      // Trier par date décroissante (plus récent en premier)
      const sortedMessages = response.data.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setMessages(sortedMessages);
    } catch (error) {
      console.error('Erreur lors de la récupération des messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Page de connexion
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-sm shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#F25F19] rounded-sm flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Administration</h1>
            <p className="text-[#71717A] mt-2">PRO HOME Développement</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Mot de passe
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez le mot de passe"
                className="w-full"
                required
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-[#F25F19] hover:bg-[#CC480B] text-white font-bold py-4 rounded-sm"
              disabled={loginLoading}
            >
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Page admin
  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <header className="bg-[#0A0A0A] text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Administration</h1>
            <p className="text-[#71717A] text-sm">Messages de contact</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchMessages}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#0A0A0A]"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#F25F19] text-[#F25F19] hover:bg-[#F25F19] hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="bg-white p-6 rounded-sm shadow-sm mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#F25F19] rounded-sm flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A0A0A]">{messages.length}</p>
              <p className="text-[#71717A]">Messages reçus</p>
            </div>
          </div>
        </div>

        {/* Messages List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#F25F19]" />
            <p className="mt-4 text-[#71717A]">Chargement...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="bg-white p-12 rounded-sm text-center">
            <MessageSquare className="w-16 h-16 text-[#E4E4E7] mx-auto mb-4" />
            <p className="text-[#71717A] text-lg">Aucun message pour le moment</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={message.id || index}
                className="bg-white p-6 rounded-sm shadow-sm border-l-4 border-[#F25F19]"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    {/* Contact Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-[#F25F19]" />
                        <span className="font-medium">
                          {message.prenom || ''} {message.nom}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-[#F25F19]" />
                        <a href={`tel:${message.telephone}`} className="hover:text-[#F25F19]">
                          {message.telephone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#F25F19]" />
                        <a href={`mailto:${message.email}`} className="hover:text-[#F25F19]">
                          {message.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-[#71717A]">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">{formatDate(message.timestamp)}</span>
                      </div>
                    </div>

                    {/* Question */}
                    <div className="bg-[#F4F4F5] p-4 rounded-sm">
                      <p className="text-[#0A0A0A]">
                        {message.question || message.message || 'Pas de message'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
